import { useState, useEffect, useRef } from "react";


const FONTS = `
@import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&display=swap');
@keyframes fadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
@keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
* { box-sizing: border-box; margin: 0; padding: 0; }
input, textarea, button { font-family: 'DM Sans', sans-serif; }
input::placeholder, textarea::placeholder { color: #3a3a3a; }
input:focus, textarea:focus { outline: none; }
`;

function useInView(t = 0.1) {
  const ref = useRef(null);
  const [v, set] = useState(false);
  useEffect(() => {
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) set(true); }, { threshold: t });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, [t]);
  return [ref, v];
}
function Up({ children, d = 0 }) {
  const [ref, v] = useInView();
  return <div ref={ref} style={{ opacity: v?1:0, transform: v?'none':'translateY(28px)', transition: `opacity .6s ease ${d}s, transform .6s ease ${d}s` }}>{children}</div>;
}

const A = '#F59E0B', DIM = '#9CA3AF', CARD = '#0F0F0F', BORDER = '#1C1C1C';

function Label({ children }) {
  return (
    <div style={{ display:'inline-flex', alignItems:'center', gap:8, marginBottom:18 }}>
      <div style={{ width:16, height:1, background:A }} />
      <span style={{ fontSize:10, fontWeight:600, color:A, letterSpacing:'0.15em', textTransform:'uppercase', fontFamily:'DM Sans,sans-serif' }}>{children}</span>
      <div style={{ width:16, height:1, background:A }} />
    </div>
  );
}

function Ck({ color='#F59E0B' }) {
  return <svg width="15" height="15" viewBox="0 0 16 16" fill="none" style={{flexShrink:0,marginTop:2}}><circle cx="8" cy="8" r="7.5" stroke={color} strokeOpacity=".3"/><path d="M5 8l2 2 4-4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}

export default function RentalLanding() {

  const [founding] = useState(4);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [form, setForm] = useState({ name:'', business:'', phone:'', email:'', current:'' });
  const go = id => document.getElementById(id)?.scrollIntoView({ behavior:'smooth' });
  const sf = k => e => setForm(p=>({...p,[k]:e.target.value}));
  const inp = { width:'100%', padding:'11px 14px', background:'#0A0A0A', border:`1px solid ${BORDER}`, borderRadius:5, color:'#E5E7EB', fontSize:13 };

  const PAINS = [
    { n:'01', t:'Double-bookings cost you customers', b:"One spreadsheet formula error and you're making uncomfortable calls. Your reputation takes the hit." },
    { n:'02', t:'Equipment leaves without a contract', b:"Paper forms get lost. No photo, no signature, no leverage when a dispute comes." },
    { n:'03', t:'No-shows with zero warning', b:"They booked, you held the equipment, they didn't show. A reminder the day before would have caught it." },
  ];
  const FEATS = [
    { e:'📅', t:'Real-time booking calendar', b:"Customers request online. Availability updates instantly — the moment it's booked, it's gone." },
    { e:'📄', t:'Digital contracts on every order', b:'Auto-generated, signed electronically, PDF saved to the order. No paper, no gaps.' },
    { e:'💬', t:'Automatic SMS reminders', b:"Day before pickup. Day before return. Overdue alerts. No phone tag required." },
    { e:'📸', t:'Damage & condition photos', b:"Timestamped. Tied to the order. You'll never argue about a scratch you can't prove." },
    { e:'💳', t:'Invoicing & online payments', b:'Stripe-powered. Auto-generated invoices. Outstanding balances on your dashboard.' },
    { e:'👥', t:'Customer CRM built in', b:"Full rental history per client. Which ones rent regularly. Which you haven't heard from in 90 days." },
  ];
  const STEPS = [
    { t:'We configure your account', b:"Send us your equipment list and pricing. We build your catalog, configure bookings, and set up contracts. You don't touch code." },
    { t:'You review and go live', b:"We walk through it together. Most businesses are live within the same week they sign up." },
    { t:'The system runs itself', b:"Reminders fire automatically. Contracts get signed before equipment leaves. Dashboard shows what's out and what's overdue." },
  ];

  return (
    <div style={{ background:'#080808', color:'#E5E7EB', fontFamily:'DM Sans,sans-serif', overflowX:'hidden' }}>
      <style>{FONTS}</style>

      {/* NAV */}
      <nav style={{ position:'sticky', top:0, zIndex:99, display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0 36px', height:56, background:'rgba(8,8,8,0.97)', backdropFilter:'blur(14px)', borderBottom:`1px solid ${BORDER}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:3 }}>
          <span style={{ fontFamily:'Instrument Serif,serif', fontSize:19, color:'#F9FAFB' }}>iVision</span>
          <span style={{ width:5, height:5, borderRadius:'50%', background:A, margin:'0 1px 7px' }} />
          <span style={{ fontSize:9, color:DIM, letterSpacing:'0.15em', textTransform:'uppercase' }}>Rental</span>
        </div>
        <div style={{ display:'flex', gap:24, alignItems:'center' }}>
          {[['Features','features'],['Pricing','pricing'],['Demo','demo']].map(([l,id])=>(
            <span key={id} onClick={()=>go(id)} style={{ fontSize:12, color:DIM, cursor:'pointer', fontWeight:500 }}>{l}</span>
          ))}
          <a href="/login" style={{ fontSize:12, color:DIM, fontWeight:500, textDecoration:'none' }}>Login</a>
          <button onClick={()=>go('demo')} style={{ background:A, color:'#000', border:'none', padding:'7px 18px', borderRadius:5, fontSize:12, fontWeight:700, cursor:'pointer' }}>Book Demo</button>
        </div>
      </nav>

      {/* HERO */}
      <section style={{ minHeight:'88vh', display:'flex', alignItems:'center', padding:'60px 36px 50px', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:`linear-gradient(rgba(245,158,11,0.025) 1px, transparent 1px),linear-gradient(90deg, rgba(245,158,11,0.025) 1px, transparent 1px)`, backgroundSize:'64px 64px' }} />
        <div style={{ position:'absolute', top:'15%', right:'2%', width:550, height:550, background:`radial-gradient(circle, rgba(245,158,11,0.055) 0%, transparent 65%)`, pointerEvents:'none' }} />
        <div style={{ position:'relative', zIndex:1, maxWidth:720 }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:9, background:'rgba(245,158,11,0.08)', border:`1px solid rgba(245,158,11,0.2)`, borderRadius:100, padding:'6px 16px', marginBottom:32, animation:'fadeUp .5s ease both' }}>
            <span style={{ width:6, height:6, borderRadius:'50%', background:'#22C55E', boxShadow:'0 0 8px #22C55E', animation:'pulse 2s infinite' }} />
            <span style={{ fontSize:11, color:A, fontWeight:600, letterSpacing:'0.06em' }}>{founding} Founding Member spots · $199/mo locked forever</span>
          </div>
          <h1 style={{ fontFamily:'Instrument Serif,serif', fontSize:'clamp(40px,6.5vw,76px)', fontWeight:400, lineHeight:1.0, letterSpacing:'-0.03em', color:'#F9FAFB', marginBottom:24, animation:'fadeUp .5s ease .1s both' }}>
            Your rental business<br /><em style={{ color:A, fontStyle:'italic' }}>runs itself</em><br />or it runs you.
          </h1>
          <p style={{ fontSize:16, color:DIM, lineHeight:1.75, maxWidth:490, marginBottom:40, animation:'fadeUp .5s ease .2s both' }}>
            IVision is rental management software built for small operators tired of spreadsheets, paper contracts, and chasing customers who didn't show up. Bookings, contracts, SMS reminders, payments — in one system.
          </p>
          <div style={{ display:'flex', gap:12, alignItems:'center', animation:'fadeUp .5s ease .3s both' }}>
            <button onClick={()=>go('demo')} style={{ background:A, color:'#000', border:'none', padding:'14px 32px', borderRadius:7, fontSize:14, fontWeight:700, cursor:'pointer', boxShadow:`0 0 0 1px ${A},0 8px 24px rgba(245,158,11,0.22)` }}>
              See it live — free demo →
            </button>
            <span style={{ fontSize:12, color:'#6B7280' }}>No pressure. No credit card.</span>
          </div>
          <div style={{ marginTop:36, display:'flex', alignItems:'center', gap:10, animation:'fadeUp .5s ease .4s both' }}>
            <div style={{ display:'flex', gap:4 }}>
              {[1,2,3,4,5].map(i=><div key={i} style={{ width:8, height:8, borderRadius:'50%', background:i<=founding?'#22C55E':BORDER, boxShadow:i<=founding?'0 0 7px rgba(34,197,94,0.5)':'none' }}/>)}
            </div>
            <span style={{ fontSize:12, color:DIM }}><span style={{ color:'#22C55E', fontWeight:600 }}>{founding}</span> of 5 spots · after they fill, price goes to $299/mo</span>
          </div>
        </div>
      </section>

      {/* TICKER */}
      <div style={{ borderTop:`1px solid ${BORDER}`, borderBottom:`1px solid ${BORDER}`, padding:'11px 0', overflow:'hidden' }}>
        <div style={{ display:'flex', animation:'marquee 22s linear infinite', width:'max-content' }}>
          {[...Array(4)].map((_,r)=>(
            <div key={r} style={{ display:'flex' }}>
              {['Online booking storefront','Auto SMS reminders','Digital contracts','Real-time availability','Damage photo capture','Customer portal','Stripe payments','Built-in CRM','Multi-location inventory','Late fee calculator'].map((item,i)=>(
                <div key={i} style={{ display:'flex', alignItems:'center', gap:20, padding:'0 20px', whiteSpace:'nowrap' }}>
                  <span style={{ fontSize:11, color:'#6B7280', fontWeight:500 }}>{item}</span>
                  <span style={{ color:A, fontSize:8 }}>◆</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* PAIN */}
      <section style={{ padding:'90px 36px', maxWidth:1060, margin:'0 auto' }}>
        <Up><Label>The Problem</Label>
          <h2 style={{ fontFamily:'Instrument Serif,serif', fontSize:'clamp(28px,4vw,50px)', fontWeight:400, letterSpacing:'-0.03em', lineHeight:1.1, color:'#F9FAFB', marginBottom:14, maxWidth:580 }}>
            Running a rental business on gut feel and spreadsheets works — until it doesn't.
          </h2>
          <p style={{ fontSize:14, color:DIM, maxWidth:460, lineHeight:1.75, marginBottom:56 }}>Every rental shop hits the same wall. Manual tracking works at 10 items. At 40 items and 20 customers a week, it starts to crack.</p>
        </Up>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:1, background:BORDER }}>
          {PAINS.map((p,i)=>(
            <Up key={i} d={i*0.09}>
              <div style={{ padding:'36px 30px', background:'#080808', minHeight:220, transition:'background .25s' }}
                onMouseOver={e=>e.currentTarget.style.background=CARD} onMouseOut={e=>e.currentTarget.style.background='#080808'}>
                <div style={{ fontFamily:'Instrument Serif,serif', fontSize:40, color:'#374151', marginBottom:16, lineHeight:1 }}>{p.n}</div>
                <div style={{ fontSize:14, fontWeight:600, color:'#F9FAFB', marginBottom:10, lineHeight:1.35 }}>{p.t}</div>
                <div style={{ fontSize:12, color:DIM, lineHeight:1.7 }}>{p.b}</div>
              </div>
            </Up>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" style={{ padding:'90px 36px', background:CARD, borderTop:`1px solid ${BORDER}`, borderBottom:`1px solid ${BORDER}` }}>
        <div style={{ maxWidth:1060, margin:'0 auto' }}>
          <Up><Label>What's Included</Label>
            <h2 style={{ fontFamily:'Instrument Serif,serif', fontSize:'clamp(28px,4vw,50px)', fontWeight:400, letterSpacing:'-0.03em', lineHeight:1.1, color:'#F9FAFB', marginBottom:14 }}>
              Everything a rental business needs.<br /><em style={{ fontStyle:'italic', color:A }}>Nothing it doesn't.</em>
            </h2>
            <p style={{ fontSize:14, color:DIM, maxWidth:440, lineHeight:1.75, marginBottom:56 }}>No modules to unlock. No tiers that gate basic features. Every plan includes everything.</p>
          </Up>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:1, background:BORDER }}>
            {FEATS.map((f,i)=>(
              <Up key={i} d={i*0.07}>
                <div style={{ padding:'32px 28px', background:CARD, transition:'background .25s' }}
                  onMouseOver={e=>e.currentTarget.style.background='#141414'} onMouseOut={e=>e.currentTarget.style.background=CARD}>
                  <span style={{ fontSize:24, display:'block', marginBottom:14 }}>{f.e}</span>
                  <div style={{ fontSize:14, fontWeight:600, color:'#F9FAFB', marginBottom:8 }}>{f.t}</div>
                  <div style={{ fontSize:12, color:DIM, lineHeight:1.7 }}>{f.b}</div>
                </div>
              </Up>
            ))}
          </div>
        </div>
      </section>

      {/* DIFFERENTIATOR */}
      <section style={{ padding:'90px 36px' }}>
        <div style={{ maxWidth:1020, margin:'0 auto' }}>
          <Up>
            <div style={{ background:`linear-gradient(135deg,rgba(245,158,11,0.07),rgba(245,158,11,0.02))`, border:`1px solid rgba(245,158,11,0.14)`, borderRadius:14, padding:'52px 56px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:56, alignItems:'center' }}>
              <div>
                <Label>The IVision difference</Label>
                <h2 style={{ fontFamily:'Instrument Serif,serif', fontSize:'clamp(24px,3vw,40px)', fontWeight:400, letterSpacing:'-0.03em', lineHeight:1.1, color:'#F9FAFB', marginBottom:16 }}>
                  Your competitors are still on spreadsheets. You don't have to be.
                </h2>
                <p style={{ fontSize:13, color:DIM, lineHeight:1.8 }}>Most rental software is built for large chains with IT departments. IVision is built for independent operators who need things to just work — and who want to call an actual person when something's off.</p>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                {[
                  ['SMS automation',"Competitors charge extra or don't offer it. We include it. Reminders go out automatically."],
                  ['Local Las Vegas support',"You're in Las Vegas. So am I. If something breaks, you call Mike directly."],
                  ['Honest pricing','Two plans. No hidden fees. No per-user charges. No features gated behind a higher tier.'],
                  ['White-glove setup',"We configure your account with your equipment and pricing. You go live without touching a settings screen."],
                ].map(([title,body],i)=>(
                  <div key={i} style={{ display:'flex', gap:12 }}>
                    <Ck />
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color:'#F9FAFB', marginBottom:2 }}>{title}</div>
                      <div style={{ fontSize:12, color:DIM, lineHeight:1.6 }}>{body}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Up>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section style={{ padding:'90px 36px', background:CARD, borderTop:`1px solid ${BORDER}`, borderBottom:`1px solid ${BORDER}` }}>
        <div style={{ maxWidth:860, margin:'0 auto' }}>
          <Up><Label>How It Works</Label>
            <h2 style={{ fontFamily:'Instrument Serif,serif', fontSize:'clamp(28px,4vw,50px)', fontWeight:400, letterSpacing:'-0.03em', lineHeight:1.1, color:'#F9FAFB', marginBottom:14 }}>We do the setup.<br />You run the business.</h2>
            <p style={{ fontSize:14, color:DIM, maxWidth:420, lineHeight:1.75, marginBottom:56 }}>Most businesses are fully live within the same week they sign up. No developers, no lengthy onboarding.</p>
          </Up>
          <div style={{ display:'flex', flexDirection:'column', gap:1, background:BORDER }}>
            {STEPS.map((s,i)=>(
              <Up key={i} d={i*0.1}>
                <div style={{ display:'grid', gridTemplateColumns:'64px 1fr', gap:28, padding:'36px 40px', background:CARD, transition:'background .25s' }}
                  onMouseOver={e=>e.currentTarget.style.background='#141414'} onMouseOut={e=>e.currentTarget.style.background=CARD}>
                  <div style={{ fontFamily:'Instrument Serif,serif', fontSize:44, color:'#374151', lineHeight:1 }}>0{i+1}</div>
                  <div>
                    <div style={{ fontSize:17, fontWeight:600, color:'#F9FAFB', marginBottom:8, letterSpacing:'-0.01em' }}>{s.t}</div>
                    <div style={{ fontSize:13, color:DIM, lineHeight:1.8, maxWidth:520 }}>{s.b}</div>
                  </div>
                </div>
              </Up>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section style={{ padding:'90px 36px' }}>
        <div style={{ maxWidth:900, margin:'0 auto' }}>
          <Up><Label>How We Compare</Label>
            <h2 style={{ fontFamily:'Instrument Serif,serif', fontSize:'clamp(24px,3.5vw,44px)', fontWeight:400, letterSpacing:'-0.03em', lineHeight:1.1, color:'#F9FAFB', marginBottom:44 }}>Half the price. More automation.<br />Someone you can actually reach.</h2>
          </Up>
          <Up d={0.1}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ borderBottom:`1px solid ${BORDER}` }}>
                <th style={{ padding:'11px 18px', textAlign:'left', fontSize:10, color:DIM, letterSpacing:'0.12em', textTransform:'uppercase', fontWeight:500 }}>Feature</th>
                {['IVision','EZRentOut','Point of Rental','Booqable'].map(h=>(
                  <th key={h} style={{ padding:'11px 14px', textAlign:'center', fontSize:11, fontWeight:700, color:h==='IVision'?A:DIM }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {[
                  ['Monthly price','$199','$300+','$400+','$99–299*'],
                  ['Auto SMS reminders','✓','✗','✗','✗'],
                  ['Digital contracts','✓','✓','✓','✓'],
                  ['Damage photo capture','✓','✗','✓','✗'],
                  ["Driver's license capture",'✓','✗','✗','✗'],
                  ['Recurring rentals','✓','✓','✓','✗'],
                  ['Late fee calculator','✓','✗','✗','✗'],
                  ['Per-client discount pricing','✓','✗','✗','✗'],
                  ['Public booking storefront','✓','✓','✗','✓'],
                  ['Employee portal','✓','✓','✗','✗'],
                  ['Built-in CRM','✓','✗','✗','✗'],
                  ['White-glove setup','✓','✗','✗','✗'],
                  ['Local direct support','✓','✗','✗','✗'],
                  ['Perpetual license','✓','✗','✗','✗'],
                ].map((row,i)=>(
                  <tr key={i} style={{ borderBottom:`1px solid ${BORDER}` }}
                    onMouseOver={e=>e.currentTarget.style.background=CARD} onMouseOut={e=>e.currentTarget.style.background='transparent'}>
                    {row.map((cell,j)=>(
                      <td key={j} style={{ padding:'11px 18px', fontSize:12, textAlign:j===0?'left':'center', color:j===0?'#9CA3AF':j===1?A:cell==='✓'?'#22C55E':cell==='✗'?'#4B5563':'#9CA3AF', fontWeight:j===1?600:400 }}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ fontSize:10, color:'#4B5563', marginTop:8, paddingLeft:18 }}>* Booqable's higher tiers can exceed $3,500/year as you scale.</p>
          </Up>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" style={{ padding:'90px 36px', background:CARD, borderTop:`1px solid ${BORDER}`, borderBottom:`1px solid ${BORDER}` }}>
        <div style={{ maxWidth:760, margin:'0 auto' }}>
          <Up><div style={{ textAlign:'center', marginBottom:56 }}>
            <Label>Pricing</Label>
            <h2 style={{ fontFamily:'Instrument Serif,serif', fontSize:'clamp(28px,4vw,50px)', fontWeight:400, letterSpacing:'-0.03em', color:'#F9FAFB', marginBottom:10 }}>Two plans. No surprises.</h2>
            <p style={{ fontSize:14, color:DIM }}>Everything included in both. The only difference is when you sign up.</p>
          </div></Up>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:2, background:BORDER }}>
            <Up d={0}>
              <div style={{ padding:'44px 36px', background:'#080808', position:'relative' }}>
                <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:'linear-gradient(90deg,#22C55E,rgba(34,197,94,0.15))' }} />
                <div style={{ display:'inline-flex', alignItems:'center', gap:5, background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:100, padding:'4px 11px', marginBottom:16 }}>
                  <span style={{ width:5, height:5, borderRadius:'50%', background:'#22C55E', animation:'pulse 2s infinite' }} />
                  <span style={{ fontSize:9, color:'#22C55E', fontWeight:600, letterSpacing:'0.1em', textTransform:'uppercase' }}>{founding} spots remaining</span>
                </div>
                <div style={{ fontSize:11, color:'#22C55E', fontWeight:600, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:10 }}>Founding Member</div>
                <div style={{ display:'flex', alignItems:'baseline', gap:4, marginBottom:4 }}>
                  <span style={{ fontFamily:'Instrument Serif,serif', fontSize:52, color:'#F9FAFB', lineHeight:1, letterSpacing:'-0.04em' }}>$199</span>
                  <span style={{ fontSize:13, color:DIM }}>/month</span>
                </div>
                <p style={{ fontSize:11, color:'#22C55E', marginBottom:28, fontWeight:500 }}>Locked in for as long as you're a customer.</p>
                {['Full platform — no feature limits','White-glove setup included','Direct access — you call Mike','Cancel any time','Your price never increases'].map(f=>(
                  <div key={f} style={{ display:'flex', gap:9, alignItems:'flex-start', marginBottom:10 }}>
                    <Ck color="#22C55E"/><span style={{ fontSize:12, color:'#D1D5DB', lineHeight:1.5 }}>{f}</span>
                  </div>
                ))}
                <button onClick={()=>go('demo')} style={{ width:'100%', padding:'13px', background:'#22C55E', color:'#000', border:'none', borderRadius:7, fontSize:13, fontWeight:700, cursor:'pointer', marginTop:6 }}>
                  Claim a Founding Spot →
                </button>
              </div>
            </Up>
            <Up d={0.1}>
              <div style={{ padding:'44px 36px', background:'#080808' }}>
                <div style={{ height:26, marginBottom:16 }} />
                <div style={{ fontSize:11, color:DIM, fontWeight:600, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:10 }}>Standard</div>
                <div style={{ display:'flex', alignItems:'baseline', gap:4, marginBottom:4 }}>
                  <span style={{ fontFamily:'Instrument Serif,serif', fontSize:52, color:'#F9FAFB', lineHeight:1, letterSpacing:'-0.04em' }}>$299</span>
                  <span style={{ fontSize:13, color:DIM }}>/month</span>
                </div>
                <p style={{ fontSize:11, color:DIM, marginBottom:28 }}>Available after founding spots fill.</p>
                {['Full platform — no feature limits','White-glove setup included','Direct access — you call Mike','Cancel any time','All future updates included'].map(f=>(
                  <div key={f} style={{ display:'flex', gap:9, alignItems:'flex-start', marginBottom:10 }}>
                    <Ck color={A}/><span style={{ fontSize:12, color:'#D1D5DB', lineHeight:1.5 }}>{f}</span>
                  </div>
                ))}
                <button onClick={()=>go('demo')} style={{ width:'100%', padding:'13px', background:'transparent', color:A, border:`1px solid rgba(245,158,11,0.3)`, borderRadius:7, fontSize:13, fontWeight:700, cursor:'pointer', marginTop:6 }}>
                  Book a Demo →
                </button>
              </div>
            </Up>
          </div>
          <p style={{ textAlign:'center', fontSize:11, color:'#4B5563', marginTop:18 }}>Prefer to own it outright? Ask about a perpetual license during your demo.</p>
        </div>
      </section>

      {/* DEMO */}
      <section id="demo" style={{ padding:'90px 36px' }}>
        <div style={{ maxWidth:640, margin:'0 auto' }}>
          <Up>
            <Label>Book Your Demo</Label>
            <h2 style={{ fontFamily:'Instrument Serif,serif', fontSize:'clamp(32px,5vw,60px)', fontWeight:400, letterSpacing:'-0.03em', lineHeight:1.0, color:'#F9FAFB', marginBottom:14 }}>
              See it running a<br /><em style={{ fontStyle:'italic', color:A }}>real rental business</em><br />in 20 minutes.
            </h2>
            <p style={{ fontSize:14, color:DIM, lineHeight:1.75, marginBottom:40, maxWidth:420 }}>
              I'll show you what IVision looks like managing bookings, contracts, and reminders for a business like yours. If it's not the right fit, I'll tell you that too.
            </p>
          </Up>
          <Up d={0.1}>
            {submitted ? (
              <div style={{ textAlign:'center', padding:'56px 28px', background:CARD, border:`1px solid ${BORDER}`, borderRadius:10 }}>
                <div style={{ fontSize:36, marginBottom:14 }}>✓</div>
                <h3 style={{ fontFamily:'Instrument Serif,serif', fontSize:26, fontWeight:400, color:'#F9FAFB', marginBottom:8 }}>Got it. I'll call you soon.</h3>
                <p style={{ fontSize:13, color:DIM }}>Usually within a few hours during business hours. — Mike</p>
              </div>
            ) : (
              <div style={{ background:CARD, border:`1px solid ${BORDER}`, borderRadius:10, padding:'40px' }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                  {[['name','Your name','Alex Johnson'],['business','Business name','Desert Equipment Rentals'],['phone','Phone number','702 555 0100'],['email','Email','alex@desert.com']].map(([k,label,ph])=>(
                    <div key={k}>
                      <label style={{ display:'block', fontSize:10, color:DIM, fontWeight:600, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:5 }}>{label}</label>
                      <input value={form[k]} onChange={sf(k)} placeholder={ph} style={inp} />
                    </div>
                  ))}
                </div>
                <div style={{ marginBottom:20 }}>
                  <label style={{ display:'block', fontSize:10, color:DIM, fontWeight:600, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:5 }}>What are you currently using?</label>
                  <textarea value={form.current} onChange={sf('current')} placeholder="Spreadsheets, paper forms, another software..." rows={3} style={{ ...inp, resize:'vertical' }} />
                </div>
                <button
                  onClick={async () => {
                    if (!form.name || !form.phone) return;
                    setSubmitting(true);
                    setSubmitError('');
                    try {
                      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/demo-leads`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ name: form.name, business: form.business, phone: form.phone, email: form.email, current: form.current }),
                      });
                      if (!res.ok) throw new Error('failed');
                      setSubmitted(true);
                    } catch (err) {
                      setSubmitError('Something went wrong. Email ivanjackm@protonmail.com directly.');
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                  disabled={submitting}
                  style={{ width:'100%', padding:'14px', background: submitting ? '#7C5A08' : A, color:'#000', border:'none', borderRadius:7, fontSize:14, fontWeight:700, cursor: submitting ? 'not-allowed' : 'pointer' }}
                >
                  {submitting ? 'Sending…' : "Request Demo — I'll reach out within 24 hours"}
                </button>
                {submitError && <p style={{ fontSize:12, color:'#EF4444', textAlign:'center', marginTop:8 }}>{submitError}</p>}
                <p style={{ fontSize:10, color:'#4B5563', textAlign:'center', marginTop:10 }}>Or email: ivanjackm@protonmail.com</p>
              </div>
            )}
          </Up>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ padding:'28px 36px', borderTop:`1px solid ${BORDER}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ display:'flex', alignItems:'center', gap:3 }}>
          <span style={{ fontFamily:'Instrument Serif,serif', fontSize:17, color:'#F9FAFB' }}>iVision</span>
          <span style={{ width:4, height:4, borderRadius:'50%', background:A, margin:'0 1px 5px' }} />
          <span style={{ fontSize:9, color:DIM, letterSpacing:'0.15em', textTransform:'uppercase' }}>Rental</span>
        </div>
        <div style={{ display:'flex', gap:16, alignItems:'center' }}>
          <span style={{ fontSize:10, color:'#4B5563' }}>IVision Marketing LLC · Las Vegas, NV</span>
          <a href="/privacy" style={{ fontSize:10, color:'#4B5563', textDecoration:'none' }}>Privacy</a>
          <a href="/terms" style={{ fontSize:10, color:'#4B5563', textDecoration:'none' }}>Terms</a>
        </div>
        <a href="/login" style={{ fontSize:11, color:DIM, textDecoration:'none' }}>Client login →</a>
      </footer>
    </div>
  );
}
